import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { ExamCategory } from "../types";
import { useUserProfile } from "./UserProfileContext";
import { EXAM_INFO, ExamInfo } from "../constants/examCatalog";

interface ExamCategoryContextType {
  examCategory: ExamCategory;
  examInfo: ExamInfo;
  isAdvanced: boolean;
  setExamCategory: (category: ExamCategory) => void;
}

const ExamCategoryContext = createContext<ExamCategoryContextType | undefined>(undefined);

const STORAGE_KEY = "kakumon-selected-category";

/**
 * 受験区分として選べる code か。β 区分（AI 生成問題だけの新制度区分。design §8a）は
 * 過去問タブの枠からだけ使い、受験区分にはしない（localStorage や DB に残っていても SC に戻す）
 */
export function isSelectableCategory(code: string | null | undefined): boolean {
  return !!code && code in EXAM_INFO && !EXAM_INFO[code].isBeta;
}

export const ExamCategoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, isLoading: profileLoading } = useUserProfile();

  const [examCategory, setExamCategoryState] = useState<ExamCategory>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSelectableCategory(saved)) return saved as ExamCategory;
    return "SC";
  });

  // DBのexamCategoryをlocalStorageと同期
  useEffect(() => {
    if (!profileLoading && isSelectableCategory(profile.examCategory)) {
      const dbCategory = profile.examCategory as ExamCategory;
      if (dbCategory !== examCategory) {
        setExamCategoryState(dbCategory);
        localStorage.setItem(STORAGE_KEY, dbCategory);
      }
    }
  }, [profile.examCategory, profileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const setExamCategory = useCallback((category: ExamCategory) => {
    setExamCategoryState(category);
    localStorage.setItem(STORAGE_KEY, category);
  }, []);

  const examInfo = EXAM_INFO[examCategory] ?? EXAM_INFO.SC;

  return (
    <ExamCategoryContext.Provider
      value={{
        examCategory,
        examInfo,
        isAdvanced: examInfo.isAdvanced,
        setExamCategory,
      }}
    >
      {children}
    </ExamCategoryContext.Provider>
  );
};

export function useExamCategory(): ExamCategoryContextType {
  const ctx = useContext(ExamCategoryContext);
  if (!ctx) throw new Error("useExamCategory must be used within ExamCategoryProvider");
  return ctx;
}

// 後方互換: 既存の import 経路（PlanSelectionPage 等）を維持するため再エクスポート
export { EXAM_INFO };
export type { ExamInfo };
